/**
 * Vaahaa Product Card Functionality
 * Handles add to cart on product cards
 */

class VaahaProductCard {
  constructor() {
    this.init();
  }

  init() {
    this.initAddToCart();
    this.initQuickView();
    this.initVideoPlayback();
  }

  initVideoPlayback() {
    // Handle video click-to-play on product cards
    document.querySelectorAll('.m-product-card__video video').forEach(video => {
      // Ensure video is clickable
      video.style.cursor = 'pointer';
      
      // Add click handler to play/pause
      video.addEventListener('click', (e) => {
        e.stopPropagation();
        if (video.paused) {
          video.play().catch(err => {
            console.error('Error playing video:', err);
          });
        } else {
          video.pause();
        }
      });

      // Ensure video displays properly
      video.addEventListener('loadedmetadata', () => {
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'cover';
      });
    });

    // Handle video component initialization
    const videoComponents = document.querySelectorAll('m-video-component.m-product-card__video');
    videoComponents.forEach(component => {
      // Wait for video to be loaded
      const observer = new MutationObserver((mutations) => {
        const video = component.querySelector('video');
        if (video) {
          // Ensure controls are visible
          if (!video.hasAttribute('controls')) {
            video.setAttribute('controls', '');
          }
          // Make sure autoplay is off
          video.removeAttribute('autoplay');
          observer.disconnect();
        }
      });
      
      observer.observe(component, { childList: true, subtree: true });
      
      // Also check immediately
      const video = component.querySelector('video');
      if (video) {
        if (!video.hasAttribute('controls')) {
          video.setAttribute('controls', '');
        }
        video.removeAttribute('autoplay');
      }
    });
  }

  initAddToCart() {
    document.querySelectorAll('.m-product-card__add-to-cart-btn').forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const productCard = button.closest('.m-product-card');
        if (!productCard) return;
        
        const variantId = button.dataset.variantId || productCard.querySelector('input[name="id"]')?.value;
        if (!variantId) return;
        
        const quantityInput = productCard.querySelector('.m-quantity__input');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

        // Disable button and show loading state
        if (button.tagName === 'BUTTON') {
          button.disabled = true;
        }
        const originalText = button.textContent;
        button.textContent = 'Adding...';
        button.style.opacity = '0.7';
        button.style.cursor = 'wait';

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: quantity
            })
          });

          if (response.ok) {
            const data = await response.json();
            
            // Show success message
            button.textContent = 'Added!';
            button.classList.add('success');
            
            // Update cart count if cart drawer exists
            this.updateCartCount();
            
            // Open cart drawer if available
            if (typeof window.MinimogTheme !== 'undefined' && window.MinimogTheme.CartDrawer) {
              window.MinimogTheme.CartDrawer.open();
            }

            // Reset button after 2 seconds
            setTimeout(() => {
              button.textContent = originalText;
              button.classList.remove('success');
              if (button.tagName === 'BUTTON') {
                button.disabled = false;
              }
              button.style.opacity = '';
              button.style.cursor = '';
              
              // Reset quantity to 1
              if (quantityInput) {
                quantityInput.value = 1;
              }
            }, 2000);
          } else {
            throw new Error('Failed to add to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          button.textContent = 'Error';
          button.classList.add('error');
          
          setTimeout(() => {
            button.textContent = originalText;
            button.classList.remove('error');
            if (button.tagName === 'BUTTON') {
              button.disabled = false;
            }
            button.style.opacity = '';
            button.style.cursor = '';
          }, 2000);
        }
      });
    });
  }

  initQuickView() {
    document.querySelectorAll('.m-button--quick-view').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const productId = button.dataset.productId;
        
        // Trigger quick view if available
        if (typeof window.MinimogTheme !== 'undefined' && window.MinimogTheme.ProductQuickView) {
          const productCard = button.closest('.m-product-card');
          const productLink = productCard?.querySelector('.m-product-card__link');
          if (productLink) {
            window.MinimogTheme.ProductQuickView.open(productLink.href);
          }
        } else {
          // Fallback: redirect to product page
          const productCard = button.closest('.m-product-card');
          const productLink = productCard?.querySelector('.m-product-card__link');
          if (productLink) {
            window.location.href = productLink.href;
          }
        }
      });
    });
  }


  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count in header
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });

      // Dispatch custom event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VaahaProductCard();
  });
} else {
  new VaahaProductCard();
}

// Reinitialize on Shopify section load (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new VaahaProductCard();
  });
}

// Also reinitialize on section reloads
document.addEventListener('shopify:section:load', () => {
  setTimeout(() => {
    new VaahaProductCard();
  }, 100);
});
